import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { endOfValidity } from "@/lib/masks";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Dumbbell, Calendar, ScanFace, TrendingUp, Plus, Camera, Star, ThumbsUp, ThumbsDown, Heart, PauseCircle, CreditCard, QrCode, Copy, CheckCircle2, Loader2, Sparkles, RefreshCw, Download, Share2, Smartphone, Bell, BellOff, CloudOff, Cloud, Apple, Utensils, Droplet, MessageSquare, Send, MessageCircle, Play, Trash2, Reply } from "lucide-react";
import MpPixDialog from "@/components/MpPixDialog";
import MpCardDialog from "@/components/MpCardDialog";
import { PortalHeader, PortalHomeScreen, PortalBottomNav } from "@/components/PortalHome";
import { AfVideoPlayer, exKey } from "@/components/AfVideoPlayer";
import { AiQuotaBadge } from "@/components/AiQuotaBadge";

// Botão que abre o vídeo do exercício direto no YouTube (app ou navegador).
// Mais confiável que iframe embed pois o listType=search do YouTube foi descontinuado.
const ExerciseVideo = ({ name, af, onPlayAf }) => {
  const query = encodeURIComponent(`${name} exercicio musculacao demonstração`);
  const searchUrl = `https://www.youtube.com/results?search_query=${query}`;
  return (
    <div className="flex flex-wrap gap-1.5">
      <a href={searchUrl} target="_blank" rel="noreferrer"
         className="mt-2 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-rose-950/50 text-rose-300 hover:bg-rose-900/60 font-bold uppercase tracking-wider transition"
         data-testid={`portal-ex-video-${name}`}>
        <Play className="w-3 h-3"/>Ver vídeo no YouTube
      </a>
      {af && (
        <button type="button" onClick={() => onPlayAf(af, name)}
          className="mt-2 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-rose-950/50 text-rose-300 hover:bg-rose-900/60 font-bold uppercase tracking-wider transition"
          data-testid={`portal-ex-af-video-${name}`}>
          <Play className="w-3 h-3"/>Vídeo American Fit
        </button>
      )}
    </div>
  );
};
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { loadFaceModels, detectEnrollFace } from "@/lib/faceapi";

export default function StudentPortal() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("home");
  const [student, setStudent] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [afVideos, setAfVideos] = useState([]);
  const [afPlay, setAfPlay] = useState(null);
  const [lastWorkout, setLastWorkout] = useState(null);
  const [lastOpen, setLastOpen] = useState(false);
  const [showAnother, setShowAnother] = useState(false);
  const [replaceId, setReplaceId] = useState(null);
  const progressReady = useRef(false);
  const [restored, setRestored] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [progress, setProgress] = useState([]);
  const [checked, setChecked] = useState({});
  const [logForm, setLogForm] = useState({});
  const [selectedExercise, setSelectedExercise] = useState(null);
  // Face update
  const [faceOpen, setFaceOpen] = useState(false);
  const [facePwd, setFacePwd] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [modelsReady, setModelsReady] = useState(false);
  const [captured, setCaptured] = useState(null);
  const [faceSaving, setFaceSaving] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  // Staff ratings
  const [staff, setStaff] = useState([]);
  const [ratingModal, setRatingModal] = useState(null); // selected staff
  const [ratingForm, setRatingForm] = useState({ reaction: "heart", comment: "" });
  const [ratingSaving, setRatingSaving] = useState(false);
  // Buy plan
  const [plans, setPlans] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [payMethod, setPayMethod] = useState(null); // "pix" | "card"
  const [pix, setPix] = useState(null);
  const [mpPixOpen, setMpPixOpen] = useState(false);
  const [mpCardOpen, setMpCardOpen] = useState(false);
  const [pixStatus, setPixStatus] = useState("pending");
  const [card, setCard] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [paidOk, setPaidOk] = useState(false);
  const pollRef = useRef(null);
  // PWA install
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pendingSync, setPendingSync] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  // Diet AI
  const [diet, setDiet] = useState(null);
  const [notices, setNotices] = useState([]);
  const [wkAIOpen, setWkAIOpen] = useState(false);
  const [wkAILoading, setWkAILoading] = useState(false);
  const [wkAIForm, setWkAIForm] = useState({ goal: "hipertrofia", experience_level: "iniciante", days_per_week: 3, focus_areas: "", notes: "" });
  const [fbOpen, setFbOpen] = useState(false);
  const [fbSending, setFbSending] = useState(false);
  const [fbForm, setFbForm] = useState({ kind: "suggestion", message: "" });
  const [myFeedbacks, setMyFeedbacks] = useState([]);
  const [unitInfo, setUnitInfo] = useState(null);
  const noticePollRef = React.useRef(null);
  const audioCtxRef = React.useRef(null);

  // Toca um beep de notificação usando Web Audio API (não precisa de arquivo)
  const playNoticeSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(()=>{});
      // Padrão "ding-dong" (2 tons)
      const now = ctx.currentTime;
      [
        { freq: 880, start: 0, dur: 0.15 },
        { freq: 660, start: 0.18, dur: 0.28 },
      ].forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + start);
        gain.gain.exponentialRampToValueAtTime(0.35, now + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + start); osc.stop(now + start + dur + 0.05);
      });
      // Vibração no celular (Android/iOS Chrome)
      try { navigator.vibrate?.([120, 60, 120]); } catch {}
    } catch {}
  };
  const [dietForm, setDietForm] = useState({ age: "", weight: "", height: "", gender: "masculino", activity_level: "moderado", goal: "emagrecer", dietary_restrictions: "", preferences: "", meals_per_day: 5 });
  const [dietLoading, setDietLoading] = useState(false);
  // Diet chat
  const [aiQuota, setAiQuota] = useState(null);

  const loadAll = async () => {
    try {
      const [s, w, a, p, st, pl, dt, nt] = await Promise.all([
        api.get(`/portal/student/${studentId}`),
        api.get(`/portal/my-workouts/${studentId}`),
        api.get(`/portal/my-attendance/${studentId}`),
        api.get(`/progress/${studentId}`),
        api.get(`/portal/staff/${studentId}`).catch(()=>({ data: [] })),
        api.get(`/portal/plans/${studentId}`).catch(()=>({ data: [] })),
        api.get(`/portal/diet/${studentId}`).catch(()=>({ data: { empty: true } })),
        api.get(`/portal/notices/${studentId}`).catch(()=>({ data: [] })),
      ]);
      setStudent(s.data); setWorkouts(w.data); setAttendance(a.data); setProgress(p.data); setStaff(st.data); setPlans(pl.data);
      setDiet(dt.data && !dt.data.empty ? dt.data : null);
      setNotices(nt.data || []);
      loadQuota();
      api.get(`/portal/workout-sessions/${studentId}`).then(({ data }) => setSessions(data || [])).catch(() => {});
      api.get("/portal/exercise-videos").then(({ data }) => setAfVideos(data || [])).catch(() => {});
      api.get(`/portal/last-workout/${studentId}`).then(({ data }) => setLastWorkout(data?.finished_at ? data : null)).catch(() => {});
      // Fetch unit details for Google review link
      if (s.data?.unit_id) {
        api.get(`/portal/unit/${s.data.unit_id}`).then(({ data }) => setUnitInfo(data)).catch(() => {});
      }
      try {
        const snapshot = { student: s.data, workouts: w.data, attendance: a.data, progress: p.data, staff: st.data, plans: pl.data, diet: dt.data, notices: nt.data, cached_at: new Date().toISOString() };
        localStorage.setItem(`fp_offline_${studentId}`, JSON.stringify(snapshot));
      } catch {}
    } catch (e) {
      try {
        const raw = localStorage.getItem(`fp_offline_${studentId}`);
        if (raw) {
          const cached = JSON.parse(raw);
          setStudent(cached.student); setWorkouts(cached.workouts || []); setAttendance(cached.attendance || []);
          setProgress(cached.progress || []); setStaff(cached.staff || []); setPlans(cached.plans || []);
          setDiet(cached.diet && !cached.diet.empty ? cached.diet : null);
          setNotices(cached.notices || []);
          toast.info(`📴 Modo offline — dados de ${new Date(cached.cached_at).toLocaleString("pt-BR")}`);
          return;
        }
      } catch {}
      toast.error("Erro ao carregar. Sem conexão e sem dados salvos.");
    }
  };

  const loadQuota = () => {
    api.get(`/portal/ai-quota/${studentId}`).then(({ data }) => setAiQuota(data)).catch(() => {});
  };

  const generateDiet = async () => {
    const age = parseInt(dietForm.age), weight = parseFloat(dietForm.weight), height = parseFloat(dietForm.height);
    if (!age || !weight || !height) return toast.error("Preencha idade, peso e altura");
    if (age < 12 || age > 100) return toast.error("Idade fora do intervalo");
    setDietLoading(true);
    try {
      const { data } = await api.post("/portal/diet/generate", {
        student_id: studentId, age, weight, height,
        gender: dietForm.gender, activity_level: dietForm.activity_level, goal: dietForm.goal,
        dietary_restrictions: dietForm.dietary_restrictions || null,
        preferences: dietForm.preferences || null,
        meals_per_day: parseInt(dietForm.meals_per_day) || 5,
      });
      setDiet(data);
      toast.success("🍽️ Sua dieta personalizada está pronta!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar dieta"); }
    finally { setDietLoading(false); loadQuota(); }
  };

  const generateWorkoutAI = async () => {
    setWkAILoading(true);
    try {
      const { data } = await api.post("/portal/workout/generate", {
        student_id: studentId,
        goal: wkAIForm.goal,
        experience_level: wkAIForm.experience_level,
        days_per_week: parseInt(wkAIForm.days_per_week) || 3,
        focus_areas: wkAIForm.focus_areas || null,
        notes: wkAIForm.notes || null,
      });
      if (replaceId) {
        const ok = window.confirm(`Nova ficha gerada: "${data.title}".\n\nUsar esta nova ficha? A ficha atual será substituída.`);
        if (ok) {
          await api.delete(`/portal/workout/${studentId}/${replaceId}`).catch(() => {});
          setWorkouts((prev) => [data, ...(prev || []).filter((w) => w.id !== replaceId)]);
          toast.success("Ficha trocada! Seu novo treino está pronto.");
        } else {
          await api.delete(`/portal/workout/${studentId}/${data.id}`).catch(() => {});
          toast.info("Troca cancelada — sua ficha atual foi mantida.");
        }
        setReplaceId(null);
        setWkAIOpen(false);
        return;
      }
      setWorkouts((prev) => [data, ...(prev || [])]);
      setWkAIOpen(false);
      toast.success("🏋️ Seu treino personalizado está pronto!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar treino"); }
    finally { setWkAILoading(false); loadQuota(); }
  };

  const shareWorkoutWhatsApp = () => {
    if (!current) return;
    const lines = [`🏋️ *${current.title}*`, current.goal ? `🎯 Objetivo: ${current.goal}` : ""];
    (current.days || []).forEach((d) => {
      lines.push("", `━━━━━━━━━━━━`, `*Ficha ${d.label}* — ${d.focus || ""}`);
      (d.exercises || []).forEach((ex, i) => {
        lines.push(`${i + 1}. *${ex.name}* — ${ex.sets}x${ex.reps}${ex.load ? ` @ ${ex.load}` : ""}`);
        lines.push(`   ⏱️ descanso ${ex.rest_seconds || 60}s${ex.notes ? ` · ${ex.notes}` : ""}`);
      });
    });
    lines.push("", "_Gerado pelo app American Fit 💪_");
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const switchWorkout = () => {
    if (!current) return;
    if (!window.confirm(`Trocar treino?\n\nA ficha atual "${current.title}" será substituída pela nova que você gerar com a IA. Se a geração falhar ou você cancelar, a ficha atual continua.`)) return;
    setReplaceId(current.id);
    setWkAIOpen(true);
  };

  const submitFeedback = async () => {
    const msg = (fbForm.message || "").trim();
    if (msg.length < 3) return toast.error("Escreva sua mensagem");
    setFbSending(true);
    try {
      await api.post("/portal/feedback", { student_id: studentId, kind: fbForm.kind, message: msg });
      toast.success("✅ Enviado! A academia recebeu sua mensagem.");
      setFbOpen(false);
      setFbForm({ kind: "suggestion", message: "" });
      loadMyFeedbacks();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar"); }
    finally { setFbSending(false); }
  };

  const loadMyFeedbacks = async () => {
    try {
      const { data } = await api.get(`/portal/feedback/${studentId}`);
      setMyFeedbacks(data || []);
      // Toca som se houver resposta nova não lida
      const unreadReply = (data || []).find(f => f.admin_reply && !f.student_reply_read);
      if (unreadReply) {
        const KEY = `fp_last_reply_${studentId}`;
        const seen = localStorage.getItem(KEY);
        if (seen !== unreadReply.id) {
          playNoticeSound();
          toast.success("💬 A academia respondeu você! Toque na aba Avaliar para ver.", { duration: 6000 });
          localStorage.setItem(KEY, unreadReply.id);
        }
      }
    } catch {}
  };

  const markReplyRead = async (fb) => {
    if (fb.student_reply_read) return;
    try {
      await api.post(`/portal/feedback/${fb.id}/mark-read`);
      setMyFeedbacks((p) => p.map(x => x.id === fb.id ? { ...x, student_reply_read: true } : x));
    } catch {}
  };


  const shareDietWhatsApp = () => {
    if (!diet) return;
    const d = diet.data || {};
    const meals = (d.meals || []).map(m => {
      const items = (m.items || []).map(it => `  • ${it.food} — ${it.quantity}${it.kcal ? ` (${it.kcal} kcal)` : ""}`).join("%0A");
      return `*${m.name}${m.time ? " — " + m.time : ""}* ${m.total_kcal ? `(${m.total_kcal} kcal)` : ""}%0A${items}`;
    }).join("%0A%0A");
    const tips = (d.tips || []).map(t => `• ${t}`).join("%0A");
    const text =
`🍽️ *${d.title || "Minha dieta - American Fit"}*%0A%0A` +
`📊 *Resumo:* ${d.summary || ""}%0A` +
`🔥 *Total:* ${d.total_calories || 0} kcal/dia%0A` +
`💪 P${d.macros?.protein_g || 0}g · 🍞 C${d.macros?.carbs_g || 0}g · 🥑 G${d.macros?.fat_g || 0}g%0A` +
(d.hydration_liters ? `💧 Hidratação: ${d.hydration_liters}L/dia%0A` : "") +
`%0A${meals}` +
(tips ? `%0A%0A💡 *Dicas:*%0A${tips}` : "") +
`%0A%0A_Gerada pelo app American Fit_`;
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  useEffect(() => { loadAll(); loadMyFeedbacks(); /* eslint-disable-next-line */ }, [studentId]);

  // Polling silencioso a cada 45s para detectar novos avisos e tocar som
  useEffect(() => {
    if (!studentId) return;
    const KEY = `fp_last_notice_${studentId}`;
    const checkNew = async () => {
      try {
        const { data } = await api.get(`/portal/notices/${studentId}`);
        if (!data || data.length === 0) return;
        setNotices(data);
        const latestId = data[0].id;
        const seen = localStorage.getItem(KEY);
        if (seen && seen !== latestId) {
          playNoticeSound();
          toast.success("📣 Novo aviso da academia — toque na aba Avisos", { duration: 6000 });
        }
        localStorage.setItem(KEY, latestId);
      } catch {}
    };
    // Salva o mais recente na primeira carga para evitar tocar som ao logar
    (async () => {
      try {
        const raw = localStorage.getItem(KEY);
        if (!raw && notices.length > 0) localStorage.setItem(KEY, notices[0].id);
      } catch {}
    })();
    noticePollRef.current = setInterval(checkNew, 45000);
    const onFocus = () => checkNew();
    window.addEventListener("focus", onFocus);
    return () => {
      if (noticePollRef.current) clearInterval(noticePollRef.current);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line
  }, [studentId, notices.length]);
  useEffect(() => () => { streamRef.current?.getTracks().forEach(t=>t.stop()); if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ===== PWA install =====
  useEffect(() => {
    if (typeof window === "undefined") return;
    const inStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
    setInstalled(inStandalone);
    if (window.__americanFitInstallPrompt) setInstallAvailable(true);
    const onAvail = () => setInstallAvailable(true);
    const onInstalled = () => { setInstallAvailable(false); setInstalled(true); toast.success("American Fit instalado!"); };
    window.addEventListener("pwa-install-available", onAvail);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-install-available", onAvail);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const isIOS = typeof window !== "undefined" && /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

  // ===== Push notifications =====
  const urlB64ToUint8Array = (b64String) => {
    const padding = "=".repeat((4 - b64String.length % 4) % 4);
    const base64 = (b64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  };
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration("/");
        if (!reg) return;
        const existing = await reg.pushManager.getSubscription();
        setPushEnabled(!!existing);
      } catch {}
    })();
  }, [studentId]);

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error("Este navegador não suporta notificações"); return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Permita as notificações nas configurações do navegador"); return; }
      const reg = await navigator.serviceWorker.getRegistration("/");
      if (!reg) { toast.error("Service worker ainda carregando, tente de novo em 5s"); return; }
      const { data: keyRes } = await api.get("/portal/push/public-key");
      if (!keyRes.public_key) { toast.error("Notificações não configuradas no servidor"); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(keyRes.public_key),
      });
      await api.post("/portal/push/subscribe", { student_id: studentId, subscription: sub.toJSON() });
      setPushEnabled(true);
      toast.success("Notificações ativadas! Você receberá lembretes de vencimento.");
    } catch (e) { toast.error("Erro ao ativar notificações"); console.warn(e); }
  };
  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration("/");
      const sub = reg && await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/portal/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setPushEnabled(false);
      toast.success("Notificações desativadas");
    } catch {}
  };

  const installPWA = async () => {
    const promptEvent = window.__americanFitInstallPrompt;
    if (promptEvent) {
      try {
        promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        if (choice?.outcome === "accepted") { setInstallAvailable(false); setInstalled(true); }
      } catch (e) { console.warn(e); }
      window.__americanFitInstallPrompt = null;
      return;
    }
    if (isIOS) { setIosHelpOpen(true); return; }
    toast.info("Use o menu do navegador (⋮) → Instalar aplicativo");
  };

  // ===== Buy/Renew Plan =====
  const openBuy = (plan) => {
    setSelectedPlan(plan); setPayMethod(null); setPix(null); setCard(null); setPixStatus("pending"); setPaidOk(false);
    setPayOpen(true);
  };
  const closePay = () => {
    // Cancel pending transaction on the server if user closes without paying
    if (!paidOk) {
      const sid = pix?.session_id || card?.session_id;
      if (sid) api.delete(`/payments/pending/${sid}`).catch(()=>{});
    }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPayOpen(false); setPayMethod(null); setPix(null); setCard(null); setSelectedPlan(null); setPaidOk(false);
  };
  const startPixMp = () => { setMpPixOpen(true); };
  const startPix = async () => {
    if (!selectedPlan) return;
    setPayLoading(true);
    try {
      const { data } = await api.post("/kiosk/pix/create", { student_id: studentId, plan_id: selectedPlan.id });
      setPix(data); setPayMethod("pix");
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/kiosk/pix/status/${data.session_id}`);
          setPixStatus(st.payment_status);
          if (st.payment_status === "paid") {
            clearInterval(pollRef.current); pollRef.current = null;
            setPaidOk(true); loadAll();
          }
        } catch {}
      }, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setPayLoading(false); }
  };
  const startCard = async () => {
    if (!selectedPlan) return;
    setPayLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/portal/card/create", { student_id: studentId, plan_id: selectedPlan.id, origin_url: origin });
      setCard(data); setPayMethod("card");
      if (data.checkout_url) {
        window.open(data.checkout_url, "_blank");
        // Poll status
        pollRef.current = setInterval(async () => {
          try {
            const { data: st } = await api.get(`/payments/status/${data.session_id}`);
            if (st.payment_status === "paid") {
              clearInterval(pollRef.current); pollRef.current = null;
              setPaidOk(true); loadAll();
            }
          } catch {}
        }, 3000);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setPayLoading(false); }
  };
  const confirmMockPix = async () => {
    if (!pix?.session_id) return;
    setPayLoading(true);
    try {
      await api.post(`/kiosk/pix/confirm/${pix.session_id}`);
      setPaidOk(true); loadAll();
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setPayLoading(false); }
  };
  const confirmMockCard = async () => {
    if (!card?.session_id) return;
    setPayLoading(true);
    try {
      await api.post(`/portal/card/confirm/${card.session_id}`);
      setPaidOk(true); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setPayLoading(false); }
  };

  const toggle = (key) => setChecked({ ...checked, [key]: !checked[key] });
  const current = workouts[0];
  const todayIso = new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
  const startKey = `fp_wk_started_${studentId}`;
  const sessKey = `fp_wk_session_${studentId}`;
  const [startedLabel, setStartedLabel] = useState(() => { try { return JSON.parse(localStorage.getItem(`fp_wk_session_${studentId}`) || "{}").day_label || ""; } catch { return ""; } });
  const [pickLabel, setPickLabel] = useState("");
  const doneToday = sessions.find(x => x.workout_id === current?.id && x.date === todayIso);
  const nextLabel = (() => {
    const days = current?.days || [];
    if (!days.length) return "";
    const last = sessions.find(x => x.workout_id === current.id);
    const idx = last ? days.findIndex(d => d.label === last.day_label) : -1;
    return days[(idx + 1) % days.length].label;
  })();
  const todayLabel = pickLabel || nextLabel;
  const todayDay = current?.days?.find(d => d.label === todayLabel);
  const activeDay = startedLabel ? current?.days?.find(d => d.label === startedLabel) : null;
  const afFor = (ex) => afVideos.find(v => (ex.exercise_id && v.exercise_id === ex.exercise_id) || v.exercise_key === exKey(ex.name));
  const readSess = () => { try { return JSON.parse(localStorage.getItem(sessKey) || "null"); } catch { return null; } };
  const startToday = () => {
    const s = { session_id: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`), workout_id: current.id, day_label: todayLabel };
    localStorage.setItem(sessKey, JSON.stringify(s));
    localStorage.removeItem(startKey);
    setChecked({}); setLogForm({}); setShowAnother(false);
    setStartedLabel(todayLabel);
  };
  // Restaura andamento salvo (celular ou servidor) ao abrir o portal
  useEffect(() => {
    if (!current || progressReady.current) return;
    progressReady.current = true;
    const apply = (s) => {
      if (!s?.session_id || s.workout_id !== current.id) return;
      setChecked(s.checked || {}); setLogForm(s.entries || {}); setStartedLabel(s.day_label);
      localStorage.setItem(sessKey, JSON.stringify(s));
    };
    const local = readSess();
    if (local?.session_id) { apply(local); setRestored(true); }
    else api.get(`/portal/workout-progress/${studentId}`).then(({ data }) => apply(data)).catch(() => {}).finally(() => setRestored(true));
    // eslint-disable-next-line
  }, [current?.id]);
  // Salvamento automático do andamento (marcações + kg/reps/séries)
  useEffect(() => {
    if (!startedLabel || !restored) return;
    const s = readSess();
    if (!s?.session_id) return;
    const next = { ...s, checked, entries: logForm };
    localStorage.setItem(sessKey, JSON.stringify(next));
    const t = setTimeout(() => {
      api.put(`/portal/workout-progress/${studentId}`, { workout_id: next.workout_id, day_label: next.day_label, session_id: next.session_id, checked, entries: logForm }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [checked, logForm, startedLabel, restored]);
  const finishDay = async (label) => {
    const day = current.days.find(d => d.label === label);
    const total = day?.exercises.length || 0;
    const done = (day?.exercises || []).filter((_, i) => checked[`${current.id}-${label}-${i}`]).length;
    const pending = total - done;
    const msg = pending > 0
      ? `Você concluiu ${done} de ${total} exercícios.\nAinda há ${pending} pendente(s).\n\nFinalizar o treino mesmo assim?`
      : `Todos os ${total} exercícios concluídos!\n\nFinalizar o treino ${label}?`;
    if (!window.confirm(msg)) return;
    setFinishing(true);
    try {
      const s = readSess();
      const exercises = (day?.exercises || []).map((ex, i) => ({
        name: ex.name, done: !!checked[`${current.id}-${label}-${i}`],
        weight: logForm[ex.name]?.weight || "", reps: logForm[ex.name]?.reps || "", sets: logForm[ex.name]?.sets || "",
      }));
      const { data } = await api.post(`/portal/workout/${studentId}/${current.id}/finish`, { day_label: label, session_id: s?.session_id, exercises });
      setSessions((prev) => [data, ...prev.filter(x => x.id !== data.id)]);
      localStorage.removeItem(sessKey); setStartedLabel(""); setPickLabel(""); setChecked({}); setLogForm({}); setShowAnother(false);
      api.get(`/portal/last-workout/${studentId}`).then(({ data: lw }) => setLastWorkout(lw?.finished_at ? lw : null)).catch(() => {});
      toast.success(`Treino ${label} finalizado!`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao finalizar treino"); }
    finally { setFinishing(false); }
  };

  const logProgress = async (exerciseName) => {
    const key = exerciseName;
    const data = logForm[key] || {};
    if (!data.weight || !data.reps) return toast.error("Preencha carga e reps");
    const payload = {
      student_id: studentId, exercise: exerciseName,
      weight: parseFloat(data.weight), reps: parseInt(data.reps), sets: parseInt(data.sets || 1),
      client_timestamp: new Date().toISOString(),
    };
    // Offline-first: always queue then try to sync
    if (!navigator.onLine) {
      queueProgress(payload);
      toast.info("📴 Salvo offline — sincroniza quando voltar a internet");
      setLogForm({ ...logForm, [key]: {} });
      return;
    }
    try {
      await api.post("/progress", payload);
      toast.success("Registrado!");
      setLogForm({ ...logForm, [key]: {} });
      loadAll();
    } catch {
      queueProgress(payload);
      toast.info("📴 Sem conexão — salvo localmente para enviar depois");
      setLogForm({ ...logForm, [key]: {} });
    }
  };

  // ===== Offline sync queue for progress logs =====
  const QUEUE_KEY = `fp_progress_queue_${studentId}`;
  const readQueue = () => {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
  };
  const writeQueue = (arr) => {
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(arr)); setPendingSync(arr.length); } catch {}
  };
  const queueProgress = (payload) => {
    const q = readQueue();
    q.push({ ...payload, _queued_at: new Date().toISOString(), _local_id: Math.random().toString(36).slice(2) });
    writeQueue(q);
  };
  const syncQueue = async () => {
    if (syncing || !navigator.onLine) return;
    const q = readQueue();
    if (q.length === 0) return;
    setSyncing(true);
    const remaining = [];
    let sent = 0;
    for (const item of q) {
      try {
        // eslint-disable-next-line
        const { _queued_at, _local_id, ...body } = item;
        await api.post("/progress", body);
        sent++;
      } catch { remaining.push(item); }
    }
    writeQueue(remaining);
    setSyncing(false);
    if (sent > 0) {
      toast.success(`✅ ${sent} registro(s) sincronizados`);
      loadAll();
    }
  };

  useEffect(() => {
    setPendingSync(readQueue().length);
    const onOnline = () => { setIsOnline(true); syncQueue(); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    // Try sync on mount (if online) and every 60s
    if (navigator.onLine) syncQueue();
    const t = setInterval(() => { if (navigator.onLine) syncQueue(); }, 60_000);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); clearInterval(t); };
    // eslint-disable-next-line
  }, [studentId]);

  // ===== Face update =====
  const openFace = async () => {
    setFaceOpen(true); setCaptured(null); setFacePwd("");
    if (!modelsReady) { try { await loadFaceModels(); setModelsReady(true); } catch { toast.error("Erro carregando modelos faciais"); } }
  };
  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setStreaming(true);
    } catch { toast.error("Não foi possível abrir a câmera. Verifique a permissão da câmera no navegador."); }
  };
  useEffect(() => {
    const v = videoRef.current;
    if (!streaming || !v || !streamRef.current) return;
    v.srcObject = streamRef.current;
    v.play().catch(() => {});
  }, [streaming]);
  const stopCam = () => { streamRef.current?.getTracks().forEach(t=>t.stop()); streamRef.current = null; setStreaming(false); };
  const captureFace = async () => {
    if (!videoRef.current) return;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth; canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const photo = canvas.toDataURL("image/jpeg", 0.85);
    const descriptor = await detectEnrollFace(videoRef.current);
    if (descriptor.problem) return toast.error(descriptor.problem);
    setCaptured({ photo, descriptor: descriptor.descriptor });
    stopCam();
  };
  const saveFace = async () => {
    if (!captured) return toast.error("Capture o rosto primeiro");
    if (!window.confirm("Deseja salvar sua nova foto facial?")) return;
    setFaceSaving(true);
    try {
      await api.post(`/portal/update-face/${studentId}`, { face_photo: captured.photo, face_embedding: captured.descriptor });
      toast.success("Facial atualizada com sucesso!");
      setFaceOpen(false); setCaptured(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setFaceSaving(false); }
  };

  // ===== Rate staff =====
  const openRating = (s) => { setRatingModal(s); setRatingForm({ reaction: "heart", comment: "" }); };
  const sendRating = async () => {
    setRatingSaving(true);
    try {
      await api.post("/portal/staff-rating", { student_id: studentId, staff_id: ratingModal.id, reaction: ratingForm.reaction, comment: ratingForm.comment });
      toast.success("Obrigado pela avaliação!");
      setRatingModal(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setRatingSaving(false); }
  };

  const exerciseNames = [...new Set(progress.map(p => p.exercise))];
  const chartData = selectedExercise
    ? progress.filter(p => p.exercise === selectedExercise).map(p => ({
        date: new Date(p.timestamp).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        weight: p.weight,
      }))
    : [];

  if (!student) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">Carregando...</div>;

  const openTab = (t) => { setTab(t); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const logout = () => {
    localStorage.removeItem("fp_portal_token"); localStorage.removeItem("fp_student");
    navigate("/portal/login", { replace: true });
  };
  const TAB_TITLES = { workout: "Meu Treino", progress: "Evolução", history: "Presenças", diet: "Dieta IA", plan: "Meu Plano", rate: "Avaliar", notices: "Avisos" };

  const headerExtras = (
    <>
            {!isOnline && (
              <div className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 font-bold" data-testid="portal-offline-badge">
                <CloudOff className="w-3.5 h-3.5"/>Offline
              </div>
            )}
            {pendingSync > 0 && (
              <button onClick={syncQueue} disabled={syncing || !isOnline} data-testid="portal-sync-badge" className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-sky-500/20 text-sky-300 font-bold hover:bg-sky-500/30" title="Toque para sincronizar agora">
                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Cloud className="w-3.5 h-3.5"/>}
                {pendingSync} p/ enviar
              </button>
            )}
            {"Notification" in window && "PushManager" in window && (
              <button onClick={pushEnabled ? disablePush : enablePush} data-testid={pushEnabled ? "portal-push-off" : "portal-push-on"} title={pushEnabled ? "Alertas ativos" : "Ativar alertas"} className={`w-8 h-8 rounded-full flex items-center justify-center ${pushEnabled ? "bg-emerald-600" : "bg-slate-800 border border-slate-700"}`}>
                {pushEnabled ? <Bell className="w-4 h-4"/> : <BellOff className="w-4 h-4"/>}
              </button>
            )}
            {!installed && (installAvailable || isIOS) && (
              <button onClick={installPWA} data-testid="portal-pwa-install-button" title="Instalar app" className="w-8 h-8 rounded-full bg-rose-600 flex items-center justify-center">
                <Download className="w-4 h-4"/>
              </button>
            )}
    </>
  );

  return (
    <div className="text-white bg-[radial-gradient(ellipse_at_top_left,rgba(159,18,57,.35),transparent_45%),radial-gradient(ellipse_at_bottom_right,rgba(159,18,57,.25),transparent_45%)] bg-slate-950 min-h-screen pb-32" data-testid="student-portal-container">
      {tab === "home" && (
        <PortalHomeScreen student={student} onFace={openFace} onOpen={openTab} noticesCount={notices.length} onLogout={logout} headerExtras={headerExtras}/>
      )}
      <div className={`portal-sections max-w-md mx-auto px-4 pt-[max(.75rem,env(safe-area-inset-top))] pb-4 ${tab === "home" ? "hidden" : ""}`} data-testid="student-portal">
        <PortalHeader>{headerExtras}</PortalHeader>

        {tab !== "home" && (
          <div className="flex items-center gap-2 mb-2 mt-3">
            <button onClick={() => openTab("home")} data-testid="portal-back-home" className="h-9 px-3 rounded-full bg-slate-900 border border-slate-800 text-sm font-bold hover:border-rose-600/60">← Início</button>
            <h2 className="font-heading font-bold text-2xl" data-testid="portal-section-title">{TAB_TITLES[tab]}</h2>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className={tab === "home" ? "hidden" : ""}>

          <TabsContent value="notices" className="mt-4" data-testid="portal-notices-content">
            {notices.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <div className="w-16 h-16 mx-auto rounded-full bg-slate-900 flex items-center justify-center mb-4 border border-slate-800">
                  <span className="text-3xl">📣</span>
                </div>
                <p className="text-sm">Nenhum aviso da academia ainda.</p>
              </div>
            ) : (
              <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-4" data-testid="portal-notices-list">
                {notices.map((n) => (
                  <Card key={n.id} className="overflow-hidden bg-slate-900 border-slate-800" data-testid={`portal-notice-${n.id}`}>
                    <img src={n.image} alt={n.caption || "Aviso"} className="w-full max-h-[500px] object-cover"/>
                    {n.caption && <div className="p-4"><p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{n.caption}</p></div>}
                    <div className="px-4 pb-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      {new Date(n.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="workout" className="mt-4">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="text-xs uppercase tracking-widest text-rose-400 font-bold">Seu treino</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {current && (
                  <>
                    <Button size="sm" onClick={shareWorkoutWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-[10px] px-2 h-8" data-testid="portal-share-workout-whatsapp">
                      <MessageCircle className="w-3 h-3 mr-1"/>WhatsApp
                    </Button>
                    <Button size="sm" onClick={switchWorkout} variant="outline" className="border-rose-500/40 bg-rose-950/40 text-rose-300 hover:bg-rose-900/50 font-bold uppercase text-[10px] px-2 h-8" data-testid="portal-switch-workout">
                      <RefreshCw className="w-3 h-3 mr-1"/>Trocar treino
                    </Button>
                  </>
                )}
                <AiQuotaBadge q={aiQuota?.workout} resetsOn={aiQuota?.resets_on} testid="portal-workout-ai-quota" />
                <Button size="sm" onClick={()=>setWkAIOpen(true)} disabled={aiQuota?.workout?.remaining === 0} className="bg-gradient-to-r from-rose-600 to-fuchsia-600 hover:from-rose-700 hover:to-fuchsia-700 text-white font-black uppercase text-[10px] px-3 h-8" data-testid="portal-generate-workout-ai">
                  <Sparkles className="w-3 h-3 mr-1"/>Gerar com IA
                </Button>
              </div>
            </div>
            {!current && <p className="text-slate-400 text-center py-8">Nenhum treino atribuído ainda — toque em <b>Gerar com IA</b> para criar seu primeiro treino personalizado.</p>}
            {current && (
              <>
                <h2 className="font-heading text-xl font-bold mb-1 text-white">{current.title}</h2>
                <p className="text-sm text-slate-400 mb-4">{current.goal}</p>
                {lastWorkout && !activeDay && (
                  <Card className="p-3 mb-3 bg-slate-900 border border-slate-800" data-testid="portal-last-workout">
                    <button type="button" onClick={()=>setLastOpen(o=>!o)} className="w-full text-left flex items-center justify-between gap-2" data-testid="portal-last-workout-toggle">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold">Último treino realizado</div>
                        <div className="text-sm text-white font-semibold">Treino {lastWorkout.day_label} · {new Date(lastWorkout.finished_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {lastWorkout.done_count}/{lastWorkout.total} exercícios</div>
                      </div>
                      <span className="text-xs text-slate-400">{lastOpen ? "Fechar" : "Ver"}</span>
                    </button>
                    {lastOpen && (
                      <div className="mt-2 space-y-1" data-testid="portal-last-workout-details">
                        {(lastWorkout.exercises || []).map((e, i) => (
                          <div key={i} className="flex items-center justify-between text-xs border-t border-slate-800 pt-1">
                            <span className={e.done ? "text-white" : "text-slate-500"}>{e.done ? "✓ " : "– "}{e.name}</span>
                            <span className="text-slate-300 font-mono">{[e.weight && `${e.weight}kg`, e.reps && `${e.reps} reps`, e.sets && `${e.sets} séries`].filter(Boolean).join(" · ") || "—"}</span>
                          </div>
                        ))}
                        <div className="text-[10px] text-slate-500 pt-1">Disponível até o fim do dia seguinte ao treino.</div>
                      </div>
                    )}
                  </Card>
                )}
                {doneToday && !activeDay && !showAnother && (
                  <Card className="p-5 bg-emerald-950/40 border border-emerald-600 text-center" data-testid="portal-today-done">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2"/>
                    <div className="font-heading font-black uppercase text-white">Treino de hoje finalizado</div>
                    <div className="text-sm text-emerald-300 mt-1">Treino {doneToday.day_label}{current.days.find(d => d.label === doneToday.day_label)?.focus ? ` — ${current.days.find(d => d.label === doneToday.day_label).focus}` : ""}</div>
                    <div className="text-xs text-slate-400 mt-2">Pode fazer outro treino hoje se quiser.</div>
                    <Button onClick={()=>setShowAnother(true)} variant="outline" className="mt-3 border-emerald-600 text-emerald-300 bg-transparent hover:bg-emerald-900/40 font-bold uppercase text-xs" data-testid="portal-another-workout">Iniciar outro treino</Button>
                  </Card>
                )}
                {(!doneToday || showAnother) && !activeDay && todayDay && (
                  <Card className="p-5 bg-slate-900 border border-slate-800" data-testid="portal-today-preview">
                    <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold">Treino de hoje</div>
                    <div className="font-heading text-2xl font-black text-white mt-1" data-testid="portal-today-label">Treino {todayDay.label}</div>
                    <div className="text-sm text-slate-300">{todayDay.focus} · {todayDay.exercises.length} exercícios</div>
                    {current.days.length > 1 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 self-center mr-1">Trocar:</span>
                        {current.days.map(d => (
                          <button key={d.label} onClick={()=>setPickLabel(d.label)} data-testid={`portal-today-pick-${d.label}`}
                            className={`text-xs w-8 h-8 rounded-full font-bold transition-colors ${d.label === todayLabel ? "bg-rose-600 text-white" : "bg-slate-800 text-slate-300"}`}>{d.label}</button>
                        ))}
                      </div>
                    )}
                    <Button onClick={startToday} className="w-full h-12 mt-4 bg-rose-600 hover:bg-rose-700 text-white font-black uppercase" data-testid="portal-start-today-workout">
                      <Play className="w-4 h-4 mr-2"/>Iniciar treino de hoje
                    </Button>
                  </Card>
                )}
                  {activeDay && [activeDay].map(d => (
                    <div key={d.label} className="mt-3 space-y-2" data-testid="portal-today-workout">
                      <div className="flex items-baseline gap-2">
                        <div className="font-heading text-xl font-black text-white" data-testid="portal-active-label">Treino {d.label}</div>
                        <div className="text-xs uppercase tracking-widest text-rose-400">{d.focus}</div>
                      </div>
                      {d.exercises.map((ex, i) => {
                        const k = `${current.id}-${d.label}-${i}`;
                        const form = logForm[ex.name] || {};
                        return (
                          <Card key={k} className={`p-3 bg-slate-900 border ${checked[k] ? "border-emerald-600 opacity-70" : "border-slate-800"}`}>
                            <div className="flex items-start gap-3">
                              <Checkbox checked={!!checked[k]} onCheckedChange={()=>toggle(k)} data-testid="student-portal-exercise-checkbox" className="mt-1"/>
                              <div className="flex-1">
                                <div className={`font-semibold text-white ${checked[k] ? "line-through" : ""}`}>{ex.name}</div>
                                <div className="text-xs text-slate-400 mt-0.5 font-mono">{ex.sets}x{ex.reps} · descanso {ex.rest_seconds}s {ex.load && `· ${ex.load}`}</div>
                                <ExerciseVideo name={ex.name} af={afFor(ex)} onPlayAf={(v, n) => setAfPlay({ video: v, name: n })}/>
                                <div className="mt-2 grid grid-cols-4 gap-1">
                                  <Input placeholder="kg" type="number" value={form.weight || ""} onChange={(e)=>setLogForm({...logForm,[ex.name]:{...form,weight:e.target.value}})} className="bg-slate-950 border-slate-700 text-white text-xs h-8 col-span-1"/>
                                  <Input placeholder="reps" type="number" value={form.reps || ""} onChange={(e)=>setLogForm({...logForm,[ex.name]:{...form,reps:e.target.value}})} className="bg-slate-950 border-slate-700 text-white text-xs h-8 col-span-1"/>
                                  <Input placeholder="séries" type="number" value={form.sets || ""} onChange={(e)=>setLogForm({...logForm,[ex.name]:{...form,sets:e.target.value}})} className="bg-slate-950 border-slate-700 text-white text-xs h-8 col-span-1"/>
                                  <Button size="sm" onClick={()=>logProgress(ex.name)} data-testid="student-portal-log-progress-button" className="bg-rose-600 hover:bg-rose-700 h-8 text-xs px-2"><Plus className="w-3 h-3"/></Button>
                                </div>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                      <div className="text-center text-sm font-bold text-slate-300" data-testid="portal-workout-progress-count">
                        {d.exercises.filter((_, i) => checked[`${current.id}-${d.label}-${i}`]).length} de {d.exercises.length} exercícios concluídos
                      </div>
                      <Button onClick={()=>finishDay(d.label)} disabled={finishing} className="w-full h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase" data-testid={`portal-workout-finish-${d.label}`}>
                        {finishing ? <Loader2 className="w-5 h-5 mr-2 animate-spin"/> : <CheckCircle2 className="w-5 h-5 mr-2"/>}Finalizar treino
                      </Button>
                    </div>
                  ))}
              </>
            )}
          </TabsContent>

          <TabsContent value="progress" className="mt-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-slate-400"><TrendingUp className="w-4 h-4"/>Evolução de carga</div>
              <button onClick={() => openTab("history")} data-testid="portal-open-history" className="text-xs font-bold text-rose-400 hover:text-rose-300 flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/>Minhas presenças</button>
            </div>
            {exerciseNames.length === 0 && <p className="text-slate-500 text-center py-8">Nenhum registro ainda. Anote suas cargas na aba Treino.</p>}
            <div className="flex flex-wrap gap-2">
              {exerciseNames.map(n => (
                <button key={n} onClick={()=>setSelectedExercise(n)} data-testid="progress-exercise-chip"
                  className={`text-xs px-3 py-1 rounded-full font-semibold ${selectedExercise === n ? "bg-rose-600 text-white" : "bg-slate-900 text-slate-300 border border-slate-800"}`}>{n}</button>
              ))}
            </div>
            {selectedExercise && (
              <Card className="p-4 bg-slate-900 border-slate-800">
                <div className="text-sm font-semibold mb-2 text-white">{selectedExercise}</div>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }}/>
                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }}/>
                      <Tooltip contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#fff", fontSize: 12 }}/>
                      <Line type="monotone" dataKey="weight" stroke="#E11D48" strokeWidth={2} dot={{ fill: "#E11D48", r: 4 }}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4 space-y-2">
            <div className="flex items-center gap-2 mb-2 text-sm text-slate-400"><Calendar className="w-4 h-4"/>Últimas presenças</div>
            {attendance.length === 0 && <p className="text-slate-500 text-center py-8">Sem presenças.</p>}
            {attendance.map(a => (
              <Card key={a.id} className="p-3 bg-slate-900 border-slate-800 flex items-center justify-between">
                <div className="text-sm text-white">{new Date(a.timestamp).toLocaleString("pt-BR")}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${a.granted ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                  {a.granted ? "✓ Liberado" : "✗ Negado"}
                </span>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="diet" className="mt-4 space-y-3" data-testid="portal-diet-tab">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-400"><Utensils className="w-4 h-4 text-emerald-400"/>Dieta personalizada por IA</div>
              <AiQuotaBadge q={aiQuota?.diet} resetsOn={aiQuota?.resets_on} testid="portal-diet-ai-quota" />
              {diet && (
                <button onClick={()=>setDiet(null)} className="text-xs text-rose-400 hover:text-rose-300 uppercase font-bold" data-testid="portal-diet-new">Nova dieta</button>
              )}
            </div>
            {!diet ? (
              <>
                <p className="text-xs text-slate-500 mb-2">Preencha seus dados. Vamos calcular sua necessidade calórica e montar um cardápio equilibrado com medidas caseiras.</p>
                <Card className="p-4 bg-slate-900 border-slate-800 space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Idade</div>
                      <Input inputMode="numeric" value={dietForm.age} onChange={(e)=>setDietForm({...dietForm, age: e.target.value.replace(/\D/g,"")})} placeholder="30" className="bg-slate-950 border-slate-700 text-white" data-testid="diet-age"/>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Peso (kg)</div>
                      <Input inputMode="decimal" value={dietForm.weight} onChange={(e)=>setDietForm({...dietForm, weight: e.target.value.replace(/[^0-9.,]/g,"").replace(",",".")})} placeholder="75" className="bg-slate-950 border-slate-700 text-white" data-testid="diet-weight"/>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Altura (cm)</div>
                      <Input inputMode="numeric" value={dietForm.height} onChange={(e)=>setDietForm({...dietForm, height: e.target.value.replace(/\D/g,"")})} placeholder="175" className="bg-slate-950 border-slate-700 text-white" data-testid="diet-height"/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Sexo</div>
                      <select value={dietForm.gender} onChange={(e)=>setDietForm({...dietForm, gender: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="diet-gender">
                        <option value="masculino">Masculino</option>
                        <option value="feminino">Feminino</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Objetivo</div>
                      <select value={dietForm.goal} onChange={(e)=>setDietForm({...dietForm, goal: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="diet-goal">
                        <option value="emagrecer">Emagrecer</option>
                        <option value="manutencao">Manutenção</option>
                        <option value="ganho_massa">Ganho de massa</option>
                        <option value="definicao">Definição</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Nível de atividade</div>
                      <select value={dietForm.activity_level} onChange={(e)=>setDietForm({...dietForm, activity_level: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="diet-activity">
                        <option value="sedentario">Sedentário</option>
                        <option value="leve">Leve (1-3x/sem)</option>
                        <option value="moderado">Moderado (3-5x)</option>
                        <option value="intenso">Intenso (6-7x)</option>
                        <option value="atleta">Atleta (2x/dia)</option>
                      </select>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Refeições/dia</div>
                      <select value={dietForm.meals_per_day} onChange={(e)=>setDietForm({...dietForm, meals_per_day: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="diet-meals">
                        <option value={3}>3 refeições</option>
                        <option value={4}>4 refeições</option>
                        <option value={5}>5 refeições</option>
                        <option value={6}>6 refeições</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Restrições (opcional)</div>
                    <Input value={dietForm.dietary_restrictions} onChange={(e)=>setDietForm({...dietForm, dietary_restrictions: e.target.value})} placeholder="Ex: intolerância a lactose, vegetariano" className="bg-slate-950 border-slate-700 text-white" data-testid="diet-restrictions"/>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Preferências (opcional)</div>
                    <Input value={dietForm.preferences} onChange={(e)=>setDietForm({...dietForm, preferences: e.target.value})} placeholder="Ex: gosto de frango e arroz, não como peixe" className="bg-slate-950 border-slate-700 text-white" data-testid="diet-preferences"/>
                  </div>
                  <Button onClick={generateDiet} disabled={dietLoading || aiQuota?.diet?.remaining === 0} className="w-full bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="diet-generate">
                    {dietLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Gerando sua dieta...</> : <><Sparkles className="w-4 h-4 mr-2"/>Gerar minha dieta</>}
                  </Button>
                  <p className="text-[10px] text-slate-500 text-center">⚠️ Sugestão de IA — não substitui um nutricionista profissional.</p>
                </Card>
              </>
            ) : (
              <div className="space-y-3" data-testid="portal-diet-result">
                <Card className="p-4 bg-gradient-to-br from-emerald-900/40 to-slate-900 border-emerald-800">
                  <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold">Sua dieta</div>
                  <div className="font-heading text-lg font-black text-white mt-1">{diet.data.title}</div>
                  <p className="text-xs text-slate-300 mt-1">{diet.data.summary}</p>
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    <div className="text-center bg-slate-950/50 rounded-lg p-2">
                      <div className="text-[9px] uppercase text-slate-400">Calorias</div>
                      <div className="font-bold text-emerald-400">{diet.data.total_calories}</div>
                      <div className="text-[9px] text-slate-500">kcal</div>
                    </div>
                    <div className="text-center bg-slate-950/50 rounded-lg p-2">
                      <div className="text-[9px] uppercase text-slate-400">Proteína</div>
                      <div className="font-bold text-rose-400">{diet.data.macros?.protein_g}g</div>
                    </div>
                    <div className="text-center bg-slate-950/50 rounded-lg p-2">
                      <div className="text-[9px] uppercase text-slate-400">Carbo</div>
                      <div className="font-bold text-amber-400">{diet.data.macros?.carbs_g}g</div>
                    </div>
                    <div className="text-center bg-slate-950/50 rounded-lg p-2">
                      <div className="text-[9px] uppercase text-slate-400">Gordura</div>
                      <div className="font-bold text-sky-400">{diet.data.macros?.fat_g}g</div>
                    </div>
                  </div>
                  {diet.data.hydration_liters && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-sky-300"><Droplet className="w-4 h-4"/>Hidratação: <strong>{diet.data.hydration_liters}L/dia</strong></div>
                  )}
                </Card>
                {(diet.data.meals || []).map((m, i) => (
                  <Card key={i} className="p-3 bg-slate-900 border-slate-800" data-testid={`diet-meal-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-heading font-bold text-white">{m.name}</div>
                        {m.time && <div className="text-[10px] text-slate-400">{m.time}</div>}
                      </div>
                      {m.total_kcal && <div className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold">{m.total_kcal} kcal</div>}
                    </div>
                    <div className="space-y-1">
                      {(m.items || []).map((it, j) => (
                        <div key={j} className="flex items-start justify-between text-xs text-slate-200 border-b border-slate-800 pb-1 last:border-0">
                          <div className="flex-1">
                            <div>{it.food}</div>
                            <div className="text-slate-500 text-[10px]">{it.quantity}</div>
                          </div>
                          {it.kcal && <div className="text-slate-400 font-mono">{it.kcal} kcal</div>}
                        </div>
                      ))}
                    </div>
                    {m.notes && <div className="mt-2 text-[10px] text-amber-300 italic">💡 {m.notes}</div>}
                  </Card>
                ))}
                {(diet.data.tips || []).length > 0 && (
                  <Card className="p-3 bg-amber-950/30 border-amber-900">
                    <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-2 flex items-center gap-1"><Apple className="w-4 h-4"/>Dicas do nutricionista IA</div>
                    <ul className="space-y-1 text-xs text-amber-100">
                      {diet.data.tips.map((t, i) => <li key={i}>• {t}</li>)}
                    </ul>
                  </Card>
                )}
                <p className="text-[10px] text-slate-500 text-center">Gerada em {new Date(diet.created_at).toLocaleString("pt-BR")} · ⚠️ Sugestão de IA</p>
                <div className="grid grid-cols-1 gap-2">
                  <Button onClick={shareDietWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="diet-share-whatsapp"><MessageCircle className="w-4 h-4 mr-2"/>Enviar no WhatsApp</Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="plan" className="mt-4 space-y-3" data-testid="portal-plan-tab">
            <div className="flex items-center gap-2 mb-1 text-sm text-slate-400"><Sparkles className="w-4 h-4 text-rose-400"/>Meu plano</div>
            <Card className="p-4 bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700 mb-2">
              <div className="text-xs uppercase tracking-widest text-rose-400 font-bold">Status atual</div>
              {student.suspended ? (
                <>
                  <div className="font-heading text-2xl font-black text-amber-400 mt-1">Suspenso</div>
                  <div className="text-xs text-white/70 mt-1">{student.suspended_days_left || 0} dia(s) guardados — procure a recepção</div>
                </>
              ) : student.membership_end && endOfValidity(student.membership_end) > new Date() ? (
                <>
                  <div className="font-heading text-2xl font-black text-emerald-400 mt-1">Ativo</div>
                  <div className="text-xs text-white/70 mt-1">Válido até {new Date(student.membership_end).toLocaleDateString("pt-BR")}</div>
                </>
              ) : (
                <>
                  <div className="font-heading text-2xl font-black text-rose-400 mt-1">Vencido</div>
                  <div className="text-xs text-white/70 mt-1">Renove para voltar a treinar</div>
                </>
              )}
            </Card>
            <div className="flex items-center gap-2 mb-1 text-sm text-slate-400 mt-4"><CreditCard className="w-4 h-4"/>Comprar / Renovar plano</div>
            <p className="text-xs text-slate-500 mb-2">Escolha um plano abaixo. Pague com Pix ou Cartão direto no app.</p>
            {plans.length === 0 && <p className="text-slate-500 text-center py-6">Carregando planos...</p>}
            <div className="space-y-2">
              {plans.map(p => (
                <button key={p.id} onClick={()=>openBuy(p)} data-testid={`portal-plan-${p.id}`}
                  className="w-full p-4 rounded-xl bg-slate-900 border border-slate-800 hover:border-rose-500 active:scale-[0.98] transition text-left flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-rose-400 font-bold">Plano</div>
                    <div className="font-heading text-lg font-black">{p.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{p.duration_days ? `${p.duration_days} dia(s)` : `${p.duration_months} mês(es)`}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-2xl font-black text-emerald-400">R$ {Number(p.price).toFixed(2)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Toque para pagar</div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="rate" className="mt-4 space-y-3" data-testid="portal-rate-tab">
            {unitInfo?.google_review_url && (
              <a href={unitInfo.google_review_url} target="_blank" rel="noreferrer" data-testid="portal-google-review"
                 className="block p-4 rounded-2xl bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-400/40 hover:border-amber-300 active:scale-[0.98] transition">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0 shadow">
                    <svg viewBox="0 0 24 24" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold">Gostou daqui?</div>
                    <div className="font-heading text-sm font-black text-white leading-tight">Avalie {unitInfo.name || "sua unidade"} no Google</div>
                    <div className="text-[11px] text-white/70 mt-0.5">⭐⭐⭐⭐⭐ Ajuda muito a academia crescer!</div>
                  </div>
                </div>
              </a>
            )}

            <button onClick={()=>setFbOpen(true)} data-testid="portal-open-feedback"
              className="w-full p-4 rounded-2xl bg-gradient-to-r from-rose-950/60 to-fuchsia-950/60 border border-rose-500/40 hover:border-rose-400 active:scale-[0.98] transition text-left flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-rose-600 to-fuchsia-600 flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-white"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-rose-400 font-bold">Fale com a academia</div>
                <div className="font-heading text-sm font-black text-white leading-tight">Reclamação, sugestão ou elogio</div>
                <div className="text-[11px] text-slate-400 mt-0.5">Sua mensagem vai direto para o administrador</div>
              </div>
              <div className="text-rose-400 text-2xl">›</div>
            </button>

            {myFeedbacks.length > 0 && (
              <div className="space-y-2" data-testid="portal-my-feedbacks">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mt-2 mb-1">Minhas mensagens · {myFeedbacks.length}</div>
                {myFeedbacks.map((fb) => {
                  const meta = { complaint: { label: "Reclamação", icon: "😟", color: "text-red-400", ring: "border-red-500/30" },
                                 suggestion: { label: "Sugestão", icon: "💡", color: "text-amber-400", ring: "border-amber-500/30" },
                                 praise: { label: "Elogio", icon: "❤️", color: "text-emerald-400", ring: "border-emerald-500/30" } }[fb.kind] || { label: fb.kind, icon: "💬", color: "text-slate-400", ring: "border-slate-700" };
                  return (
                    <Card key={fb.id} className={`bg-slate-900 border ${meta.ring} p-3`} onClick={() => fb.admin_reply && !fb.student_reply_read && markReplyRead(fb)} data-testid={`portal-my-fb-${fb.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">{meta.icon}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>{meta.label}</span>
                        <span className="text-[10px] text-slate-500 ml-auto">{new Date(fb.created_at).toLocaleDateString("pt-BR")}</span>
                        {fb.resolved && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-300 font-bold uppercase">Resolvido</span>}
                      </div>
                      <p className="text-sm text-slate-200 whitespace-pre-wrap">{fb.message}</p>
                      {fb.admin_reply ? (
                        <div className={`mt-2 p-3 rounded-lg bg-gradient-to-br from-emerald-950/60 to-emerald-900/40 border ${fb.student_reply_read ? "border-emerald-700/40" : "border-emerald-400 ring-2 ring-emerald-500/30 animate-pulse-slow"}`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <Reply className="w-3 h-3 text-emerald-400"/>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300">Resposta da academia · {fb.admin_reply.author_name}</span>
                            {!fb.student_reply_read && <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-400 text-emerald-950 font-bold uppercase">Nova</span>}
                          </div>
                          <p className="text-sm text-white whitespace-pre-wrap">{fb.admin_reply.message}</p>
                          <div className="text-[9px] text-emerald-400/60 mt-1 uppercase tracking-widest font-bold">{new Date(fb.admin_reply.created_at).toLocaleString("pt-BR")}</div>
                        </div>
                      ) : (
                        <div className="mt-1 text-[10px] text-slate-500 italic">⏳ Aguardando resposta da academia...</div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            <div className="flex items-center gap-2 mb-1 mt-2 text-sm text-slate-400"><Star className="w-4 h-4 text-amber-400"/>Avalie a equipe</div>
            <p className="text-xs text-slate-500 mb-2">Toque em um funcionário para dar sua nota. Suas avaliações ajudam a academia a crescer.</p>
            {staff.length === 0 && <p className="text-slate-500 text-center py-8">Nenhum funcionário cadastrado ainda.</p>}
            <div className="grid grid-cols-2 gap-3">
              {staff.map(m => (
                <button key={m.id} onClick={()=>openRating(m)} data-testid={`portal-rate-staff-${m.id}`}
                  className="p-3 rounded-xl bg-slate-900 border border-slate-800 hover:border-rose-500 active:scale-95 transition text-left">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="w-full aspect-square rounded-lg object-cover mb-2"/>
                  ) : (
                    <div className="w-full aspect-square rounded-lg bg-slate-800 flex items-center justify-center font-bold text-2xl text-slate-400 mb-2">{m.name[0]}</div>
                  )}
                  <div className="font-semibold text-sm truncate text-white">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold">{m.role === "trainer" ? "Personal" : m.role === "reception" ? "Recepção" : m.role === "admin" ? "Admin" : m.role}</div>
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {tab !== "home" && <PortalBottomNav home={false} onHome={() => openTab("home")} onLogout={logout}/>}

      {/* Face update dialog */}
      <Dialog open={faceOpen} onOpenChange={(v)=>{ if (!v) stopCam(); setFaceOpen(v); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="portal-face-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5 text-rose-500"/>Atualizar minha facial</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {captured ? (
              <div className="flex items-center gap-3">
                <img src={captured.photo} className="w-24 h-24 rounded-lg object-cover border-2 border-emerald-500" alt=""/>
                <div>
                  <div className="text-emerald-400 text-sm font-bold">Rosto capturado ✓</div>
                  <Button size="sm" variant="outline" onClick={()=>{ setCaptured(null); startCam(); }} className="border-slate-700 text-white bg-slate-800 hover:bg-slate-700 hover:text-white mt-2">Refazer</Button>
                </div>
              </div>
            ) : streaming ? (
              <div className="space-y-2">
                <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg bg-black" data-testid="portal-face-video"/>
                <canvas ref={canvasRef} className="hidden"/>
                <div className="flex gap-2">
                  <Button size="sm" onClick={captureFace} className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase flex-1" data-testid="portal-face-capture">Capturar</Button>
                  <Button size="sm" variant="outline" onClick={stopCam} className="border-slate-700 text-white bg-slate-800 hover:bg-slate-700 hover:text-white">Cancelar</Button>
                </div>
              </div>
            ) : (
              <Button onClick={startCam} disabled={!modelsReady} className="w-full bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="portal-face-open-camera">
                {modelsReady ? <><Camera className="w-4 h-4 mr-2"/>Abrir câmera</> : "Carregando modelos..."}
              </Button>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={()=>setFaceOpen(false)} className="flex-1 border-slate-700 text-white bg-slate-800 hover:bg-slate-700 hover:text-white">Cancelar</Button>
              <Button onClick={saveFace} disabled={faceSaving || !captured} className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="portal-face-save">{faceSaving ? "Salvando..." : "Confirmar"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rating dialog */}
      <Dialog open={!!ratingModal} onOpenChange={(v)=>!v && setRatingModal(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="portal-rating-dialog">
          {ratingModal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {ratingModal.photo ? <img src={ratingModal.photo} className="w-10 h-10 rounded-full object-cover" alt=""/> : <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center font-bold">{ratingModal.name[0]}</div>}
                  Avaliar {ratingModal.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={()=>setRatingForm({...ratingForm, reaction: "heart"})} data-testid="rating-heart" className={`p-4 rounded-xl border-2 transition ${ratingForm.reaction === "heart" ? "border-rose-500 bg-rose-500/10" : "border-slate-700"}`}>
                    <Heart className="w-8 h-8 mx-auto text-rose-500"/>
                    <div className="text-xs mt-1 text-center font-bold">Coração</div>
                  </button>
                  <button onClick={()=>setRatingForm({...ratingForm, reaction: "like"})} data-testid="rating-like" className={`p-4 rounded-xl border-2 transition ${ratingForm.reaction === "like" ? "border-emerald-500 bg-emerald-500/10" : "border-slate-700"}`}>
                    <ThumbsUp className="w-8 h-8 mx-auto text-emerald-500"/>
                    <div className="text-xs mt-1 text-center font-bold">Gostei</div>
                  </button>
                  <button onClick={()=>setRatingForm({...ratingForm, reaction: "dislike"})} data-testid="rating-dislike" className={`p-4 rounded-xl border-2 transition ${ratingForm.reaction === "dislike" ? "border-slate-400 bg-slate-500/10" : "border-slate-700"}`}>
                    <ThumbsDown className="w-8 h-8 mx-auto text-slate-400"/>
                    <div className="text-xs mt-1 text-center font-bold">Não gostei</div>
                  </button>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Comentário (opcional)</div>
                  <Textarea rows={3} value={ratingForm.comment} onChange={(e)=>setRatingForm({...ratingForm, comment: e.target.value})} placeholder="Ex: sempre atencioso comigo" className="bg-slate-950 border-slate-700 text-white" data-testid="rating-comment"/>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={()=>setRatingModal(null)} className="flex-1 border-slate-700 text-white bg-slate-800 hover:bg-slate-700 hover:text-white">Cancelar</Button>
                  <Button onClick={sendRating} disabled={ratingSaving} className="flex-1 bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="rating-submit">{ratingSaving ? "Enviando..." : "Enviar avaliação"}</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pay dialog (Pix or Card) */}
      <Dialog open={payOpen} onOpenChange={(v)=>!v && closePay()}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="portal-pay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-rose-500"/>
              {paidOk ? "Pagamento confirmado" : selectedPlan?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedPlan && !paidOk && (
            <div className="p-3 mb-3 rounded-lg bg-rose-950/40 border border-rose-900 text-center">
              <div className="text-xs uppercase text-rose-300">Valor</div>
              <div className="font-heading text-3xl font-black text-rose-400">R$ {Number(selectedPlan.price).toFixed(2)}</div>
              <div className="text-xs text-white/60 mt-1">{selectedPlan.duration_days ? `${selectedPlan.duration_days} dia(s)` : `${selectedPlan.duration_months} mês(es)`}</div>
            </div>
          )}
          {paidOk ? (
            <div className="text-center py-6" data-testid="portal-pay-success">
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-2"/>
              <div className="font-heading text-xl font-bold text-emerald-300">Plano ativado!</div>
              <div className="text-sm text-slate-300 mt-1">Válido até <strong>{student.membership_end ? new Date(student.membership_end).toLocaleDateString("pt-BR") : "—"}</strong></div>
              <Button onClick={closePay} className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" data-testid="portal-pay-close">Concluir</Button>
            </div>
          ) : !payMethod ? (
            <div className="grid grid-cols-2 gap-2" data-testid="portal-pay-methods">
              <button onClick={()=>setMpPixOpen(true)} disabled={payLoading} data-testid="portal-pay-method-pix" className="p-4 rounded-xl border-2 border-slate-700 hover:border-rose-500 bg-slate-800 active:scale-95 transition">
                <QrCode className="w-8 h-8 mx-auto text-rose-400"/>
                <div className="font-bold mt-2">Pix Mercado Pago</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">QR na tela</div>
              </button>
              <button onClick={()=>setMpCardOpen(true)} disabled={payLoading} data-testid="portal-pay-method-card" className="p-4 rounded-xl border-2 border-slate-700 hover:border-emerald-500 bg-slate-800 active:scale-95 transition">
                <CreditCard className="w-8 h-8 mx-auto text-emerald-400"/>
                <div className="font-bold mt-2">Cartão de crédito</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">até {Math.max(1, selectedPlan?.max_installments || 1)}x</div>
              </button>
            </div>
          ) : payMethod === "pix" && pix ? (
            <div className="space-y-3" data-testid="portal-pay-pix">
              {pix.qr_image_url && (
                <div className="flex justify-center bg-white p-3 rounded-lg">
                  <img src={pix.qr_image_url} alt="QR Pix" className="w-56 h-56" data-testid="portal-pix-qr"/>
                </div>
              )}
              {pix.pix_code && (
                <div>
                  <div className="text-xs text-slate-400 mb-1">Copia e cola</div>
                  <div className="flex gap-2">
                    <div className="flex-1 text-[10px] font-mono bg-slate-950 p-2 rounded border border-slate-800 break-all max-h-16 overflow-y-auto">{pix.pix_code}</div>
                    <Button size="sm" variant="outline" onClick={()=>{navigator.clipboard.writeText(pix.pix_code); toast.success("Copiado");}} className="border-slate-700 text-white bg-slate-800 hover:bg-slate-700 hover:text-white"><Copy className="w-4 h-4"/></Button>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-sm text-rose-300 pt-2" data-testid="portal-pix-waiting">
                <Loader2 className="w-4 h-4 animate-spin"/><span className="uppercase text-xs tracking-widest">Aguardando pagamento...</span>
              </div>
              {pix.is_mock && (
                <Button onClick={confirmMockPix} disabled={payLoading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase" data-testid="portal-pix-mock-confirm">
                  {payLoading ? <Loader2 className="animate-spin"/> : "Simular pagamento (Demo)"}
                </Button>
              )}
              <Button variant="ghost" onClick={closePay} className="w-full text-slate-400 hover:text-white hover:bg-white/5">Cancelar</Button>
            </div>
          ) : payMethod === "card" && card ? (
            <div className="space-y-3" data-testid="portal-pay-card">
              {card.checkout_url ? (
                <>
                  <div className="p-3 rounded-lg bg-slate-800 text-center">
                    <CreditCard className="w-10 h-10 mx-auto text-emerald-400 mb-2"/>
                    <p className="text-sm text-slate-300">Abrimos o checkout seguro do Stripe em uma nova aba.</p>
                    <Button onClick={()=>window.open(card.checkout_url, "_blank")} className="mt-3 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase w-full" data-testid="portal-card-open">
                      Reabrir checkout
                    </Button>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm text-emerald-300">
                    <Loader2 className="w-4 h-4 animate-spin"/><span className="uppercase text-xs tracking-widest">Aguardando confirmação...</span>
                  </div>
                </>
              ) : (
                <div className="p-3 rounded-lg bg-amber-950/40 border border-amber-800 text-amber-200 text-xs">
                  Modo demo: o plano não tem chave Stripe configurada. Use o botão abaixo para simular o pagamento com cartão.
                </div>
              )}
              {card.is_mock && (
                <Button onClick={confirmMockCard} disabled={payLoading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase" data-testid="portal-card-mock-confirm">
                  {payLoading ? <Loader2 className="animate-spin"/> : "Simular pagamento (Demo)"}
                </Button>
              )}
              <Button variant="ghost" onClick={closePay} className="w-full text-slate-400 hover:text-white hover:bg-white/5">Cancelar</Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      {/* Fale com a academia */}
      <Dialog open={fbOpen} onOpenChange={setFbOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="portal-feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center"><MessageSquare className="w-4 h-4"/></div>
              <div>
                <div className="text-sm font-heading font-bold">Fale com a academia</div>
                <div className="text-[10px] text-slate-400 font-normal">Sua mensagem chega direto ao administrador</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-400 mb-2">Tipo de mensagem</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "complaint", label: "Reclamação", icon: "😟", color: "border-red-500 bg-red-500/10 text-red-400" },
                  { key: "suggestion", label: "Sugestão", icon: "💡", color: "border-amber-500 bg-amber-500/10 text-amber-400" },
                  { key: "praise", label: "Elogio", icon: "❤️", color: "border-emerald-500 bg-emerald-500/10 text-emerald-400" },
                ].map((k) => {
                  const active = fbForm.kind === k.key;
                  return (
                    <button key={k.key} onClick={()=>setFbForm({...fbForm, kind: k.key})}
                      className={`p-3 rounded-xl border-2 transition text-center ${active ? k.color : "border-slate-700 text-slate-500 hover:border-slate-600"}`}
                      data-testid={`portal-fb-kind-${k.key}`}>
                      <div className="text-xl mb-1">{k.icon}</div>
                      <div className="text-[10px] font-bold uppercase tracking-wider">{k.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Sua mensagem</div>
              <textarea value={fbForm.message} onChange={(e)=>setFbForm({...fbForm, message: e.target.value})} rows={5} maxLength={2000}
                placeholder="Escreva com detalhes... (mínimo 3 caracteres)"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 resize-none"
                data-testid="portal-fb-message"/>
              <div className="text-right text-[10px] text-slate-500 mt-1">{fbForm.message.length}/2000</div>
            </div>
            <Button onClick={submitFeedback} disabled={fbSending} className="w-full bg-gradient-to-r from-rose-600 to-fuchsia-600 hover:from-rose-700 hover:to-fuchsia-700 font-black uppercase" data-testid="portal-fb-submit">
              {fbSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Enviando...</> : <><Send className="w-4 h-4 mr-2"/>Enviar para a academia</>}
            </Button>
            <p className="text-[10px] text-slate-500 text-center">Sua mensagem será enviada de forma identificada (com seu nome) para o administrador da academia.</p>
          </div>
        </DialogContent>
      </Dialog>


      <AfVideoPlayer video={afPlay?.video} name={afPlay?.name} onClose={() => setAfPlay(null)} />

      {/* AI Workout Generator */}
      <Dialog open={wkAIOpen} onOpenChange={(v) => { setWkAIOpen(v); if (!v) setReplaceId(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="portal-wk-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center"><Dumbbell className="w-4 h-4"/></div>
              <div>
                <div className="text-sm font-heading font-bold">Gerar treino com IA</div>
                <div className="text-[10px] text-slate-400 font-normal">Personalizado para você em segundos</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-slate-400 mb-1">Objetivo</div>
                <select value={wkAIForm.goal} onChange={(e)=>setWkAIForm({...wkAIForm, goal: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="portal-wk-ai-goal">
                  <option value="hipertrofia">Hipertrofia (ganhar massa)</option>
                  <option value="emagrecimento">Emagrecimento</option>
                  <option value="resistencia">Resistência</option>
                  <option value="forca">Força</option>
                  <option value="condicionamento">Condicionamento geral</option>
                </select>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Nível</div>
                <select value={wkAIForm.experience_level} onChange={(e)=>setWkAIForm({...wkAIForm, experience_level: e.target.value})} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="portal-wk-ai-level">
                  <option value="iniciante">Iniciante</option>
                  <option value="intermediario">Intermediário</option>
                  <option value="avancado">Avançado</option>
                </select>
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Dias por semana</div>
              <select value={wkAIForm.days_per_week} onChange={(e)=>setWkAIForm({...wkAIForm, days_per_week: parseInt(e.target.value)})} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" data-testid="portal-wk-ai-days">
                {[2,3,4,5,6].map(n => <option key={n} value={n}>{n} dias</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Focos (opcional)</div>
              <Input value={wkAIForm.focus_areas} onChange={(e)=>setWkAIForm({...wkAIForm, focus_areas: e.target.value})} placeholder="Ex: peito, costas, pernas" className="bg-slate-950 border-slate-700 text-white" data-testid="portal-wk-ai-focus"/>
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Observações (limitações, lesões, preferências)</div>
              <Input value={wkAIForm.notes} onChange={(e)=>setWkAIForm({...wkAIForm, notes: e.target.value})} placeholder="Ex: sem impacto no joelho" className="bg-slate-950 border-slate-700 text-white" data-testid="portal-wk-ai-notes"/>
            </div>
            <AiQuotaBadge q={aiQuota?.workout} resetsOn={aiQuota?.resets_on} testid="portal-wk-ai-dialog-quota" />
            <Button onClick={generateWorkoutAI} disabled={wkAILoading || aiQuota?.workout?.remaining === 0} className="w-full bg-gradient-to-r from-rose-600 to-fuchsia-600 hover:from-rose-700 hover:to-fuchsia-700 font-black uppercase" data-testid="portal-wk-ai-submit">
              {wkAILoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Gerando treino...</> : <><Sparkles className="w-4 h-4 mr-2"/>Gerar meu treino</>}
            </Button>
            <p className="text-[10px] text-slate-500 text-center">O treino gerado será salvo automaticamente e você poderá começar a treinar agora mesmo.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* iOS Install instructions */}
      <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-sm" data-testid="portal-ios-help-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-rose-500"/>Instalar no iPhone</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-slate-200">
            <p className="text-slate-300">No iPhone/iPad basta 3 passos no Safari:</p>
            <ol className="space-y-2 list-decimal list-inside">
              <li>Toque no ícone de <strong>Compartilhar</strong> <Share2 className="inline w-4 h-4 text-sky-400"/> na barra inferior do Safari</li>
              <li>Role e toque em <strong>"Adicionar à Tela de Início"</strong></li>
              <li>Confirme em <strong>Adicionar</strong> — pronto! O American Fit aparecerá como app na sua tela.</li>
            </ol>
            <div className="p-3 rounded-lg bg-amber-950/40 border border-amber-800 text-xs text-amber-200">
              💡 Dica: use o <strong>Safari</strong> (não o Chrome no iPhone) para conseguir instalar.
            </div>
            <Button onClick={()=>setIosHelpOpen(false)} className="w-full bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="portal-ios-help-close">Entendi</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Mercado Pago Pix — pagamento pelo portal */}
      {selectedPlan && studentId && (
        <MpPixDialog
          open={mpPixOpen}
          onOpenChange={setMpPixOpen}
          plan={selectedPlan}
          createPayload={{ student_id: studentId, plan_id: selectedPlan.id }}
          endpoints={{ create: "/portal/mp/pix/create", status: "/portal/mp/pix/status/", sync: "/portal/mp/pix/sync/" }}
          onPaid={() => { setPaidOk(true); loadAll(); setPayOpen(false); }}
          title="Pagamento Pix — Mercado Pago"
        />
      )}
      {/* Mercado Pago Cartão — pagamento pelo portal */}
      {selectedPlan && studentId && (
        <MpCardDialog
          open={mpCardOpen}
          onOpenChange={setMpCardOpen}
          plan={selectedPlan}
          payerEmail={student?.email || ""}
          payerCpf={student?.cpf || ""}
          createPayload={{ student_id: studentId, plan_id: selectedPlan.id }}
          endpoints={{ create: "/portal/mp/card/create", status: "/portal/mp/card/status/", sync: "/portal/mp/card/sync/" }}
          onPaid={() => { setPaidOk(true); loadAll(); setPayOpen(false); }}
          title="Pagar com cartão — Mercado Pago"
        />
      )}
    </div>
  );
}
